import * as THREE from 'three'
import { RectAreaLightUniformsLib } from 'three/examples/jsm/lights/RectAreaLightUniformsLib.js'
import { ColladaLoader } from 'three/examples/jsm/loaders/ColladaLoader.js'
import type { Group, Scene } from 'three'

/**
 * Adds ambient light to given root
 * @param root Scene or group to add model to
 */
function addAmbientLight(root: Group | Scene, options): void {
    const color = 0xcccccc
    const intensity = options?.intensity || 1
    const ambientLight = new THREE.AmbientLight(color, intensity)
    root.add(ambientLight)
}

/**
 * Adds rectangle light to given root
 * @param root Scene or group to add model to
 */
function addRectLight(root: Group | Scene): void {
    RectAreaLightUniformsLib.init()
    const lwidth = 25
    const lheight = 50
    const lintensity = 1
    const rectLight = new THREE.RectAreaLight(0xffffcc, lintensity, lwidth, lheight)
    rectLight.position.set(9, 0, 15)
    rectLight.rotation.set(0, 0, -Math.PI / 2)
    rectLight.updateWorldMatrix()
    root.add(rectLight)
}

/**
 * Adds GridHelper to given root
 * @param root Scene or group to add model to
 */
function addGrid(root: Group | Scene): void {
    const size = 256
    const divisions = 256
    const gridHelper = new THREE.GridHelper(size, divisions, 0x000000, 0x333333)
    const gridHelperMaterial = gridHelper.material as THREE.Material //Cast needed to avoids ts errors
    gridHelper.position.set(8.5, 0, -4)
    gridHelper.rotateX(Math.PI / 2)

    gridHelperMaterial.transparent = true
    gridHelperMaterial.opacity = 0.4

    root.add(gridHelper)
}

/**
 * Adds measurement plane to given root
 * @param root Scene or group to add model to
 */
function addMeasurementPlane(root: Group | Scene) {
    const measurementPlaneSize = 32
    const measurementTextureLoader = new THREE.TextureLoader()
    const texture = measurementTextureLoader.load('/MeasurementCircle.png')

    const planeGeo = new THREE.PlaneGeometry(measurementPlaneSize, measurementPlaneSize)
    const planeMat = new THREE.MeshStandardMaterial({
        map: texture,
        side: THREE.DoubleSide,
        transparent: true,
        roughness: 0.1,
        metalness: 0,
    })

    const MeasurementDecalMesh = new THREE.Mesh(planeGeo, planeMat)
    MeasurementDecalMesh.receiveShadow = true
    MeasurementDecalMesh.rotation.x = 0
    MeasurementDecalMesh.rotation.y = 0
    MeasurementDecalMesh.rotation.z = -Math.PI / 2
    MeasurementDecalMesh.position.set(9, 0, -4)

    root.add(MeasurementDecalMesh)
}

/**
 * Adds mesh plane to given root
 * @param root Scene or group to add model to
 */
function addMeshPlane(root: Group | Scene): void {
    const floorGeometry = new THREE.BoxGeometry(2000, 0.1, 2000)
    const floorMaterial = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 0.1,
        metalness: 0,
    })
    const mshStdFloor = new THREE.Mesh(floorGeometry, floorMaterial)

    mshStdFloor.receiveShadow = true
    mshStdFloor.rotation.set(0, -Math.PI / 2, Math.PI / 2)
    mshStdFloor.position.set(0, 0, -4.1)

    root.add(mshStdFloor)
}

/**
 * Adds aircraft scene from given stlUrl to the given root scene or group
 * @param root  Scene or group to add aircraft scene to
 * @param stlUrl Url of aircraft stl file to be used in colladaLoader
 * @param loadedCallback callback function triggered when colladaLoader has finished loading the aircraft scene
 */
function addAircraft(root: Group | Scene, stlUrl, loadedCallback: () => void): void {
    const stlurl = stlUrl
    const loader = new ColladaLoader()

    const modelMaterial = new THREE.MeshStandardMaterial({
        color: 0x333333,
        opacity: 1,
        transparent: false,
        flatShading: true,
        side: THREE.DoubleSide,
        roughness: 0.9,
        metalness: 0.1,
    })

    loader.load(stlurl.toString(), (result) => {
        result.scene.traverse(function (child) {
            if (child instanceof THREE.Mesh) {
                child.material = modelMaterial
                child.geometry.computeVertexNormals()
                child.castShadow = true
                child.receiveShadow = true
            }
        })

        result.scene.name = 'aircraftModel'
        root.add(result.scene)
        if (typeof loadedCallback === "function") loadedCallback()
    })
}

/**
 * Loads all models needed for review tool visualizer scene
 * @param root Scene or group to add models to
 * @param stlUrl Url of aircraft stl file to be used in colladaLoader
 * @param loadedCallback callback function triggered when colladaLoader has finished loading the aircraft scene
 */
function addVisualizerSceneModels(root: Group | Scene, stlUrl: string, options): void {
    addAmbientLight(root, options?.ambientLightOptions)
    addRectLight(root)
    if (!options?.hideGrid) addGrid(root)
    if (!options?.hideMeshPlane) addMeshPlane(root)
    if (!options?.hidemeasurementPlane) addMeasurementPlane(root)
    addAircraft(root, stlUrl, options?.loadedCallback)
}

export { addVisualizerSceneModels }
